import React, { useState, useEffect } from 'react';
import { Button } from '@quantiix/design-system';
import { useTranslation } from '../../../i18n';
import { useToast } from '../../../contexts/ToastContext';
import { createLogger } from '../../../utils/logger';
import { COUNTRY_CODES, splitPhone, toE164 } from '../utils/countryCodes';
import shared from '../Settings.shared.module.css';
import styles from './ProfileSection.module.css';

const logger = createLogger('Shell::Settings::Profile');

function ProfileSection({ settings, editUser }) {
    const { t } = useTranslation('shell');
    const { addToast } = useToast();

    const savedSplit = splitPhone(settings.phone);

    const [phoneCountry, setPhoneCountry] = useState(savedSplit.country.code);
    const [phoneNumber, setPhoneNumber] = useState(savedSplit.localNumber);
    const [isSaving, setIsSaving] = useState(false);

    // Sync the form back to the persisted value whenever it changes (initial
    // load and after each refetch following a successful save).
    useEffect(() => {
        const split = splitPhone(settings.phone);
        setPhoneCountry(split.country.code);
        setPhoneNumber(split.localNumber);
    }, [settings.phone]);

    const displayName = (settings.firstName && settings.lastName)
        ? `${settings.firstName} ${settings.lastName}`
        : '';

    const isDirty =
        phoneNumber !== savedSplit.localNumber ||
        phoneCountry !== savedSplit.country.code;

    const handleSave = async () => {
        const newPhone = toE164(phoneCountry, phoneNumber);
        setIsSaving(true);
        try {
            await editUser({ phone: newPhone });
            logger.info('Profile saved', { phone: newPhone });
            addToast({
                type: 'success',
                message: t('settings.profile.saveSuccess'),
                duration: 3000
            });
        } catch (err) {
            logger.error('Failed to update profile', err);
            addToast({
                type: 'error',
                message: err?.message || t('settings.profile.saveError'),
                duration: 4000
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setPhoneCountry(savedSplit.country.code);
        setPhoneNumber(savedSplit.localNumber);
    };

    return (
        <div className={shared.sectionContent}>
            <div className={shared.field}>
                <label className={shared.fieldLabel}>{t('settings.profile.emailLabel')}</label>
                <div className={shared.fieldValue}>{settings.email || '-'}</div>
            </div>

            <div className={shared.field}>
                <label className={shared.fieldLabel}>{t('settings.profile.nameLabel')}</label>
                <div className={shared.fieldValue}>{displayName || '-'}</div>
            </div>

            <div className={shared.field}>
                <label className={shared.fieldLabel}>{t('settings.profile.phoneLabel')}</label>
                <div className={styles.phoneRow}>
                    <select
                        className={styles.countrySelect}
                        value={phoneCountry}
                        onChange={(e) => setPhoneCountry(e.target.value)}
                        aria-label={t('settings.profile.countryCodeAria')}
                    >
                        {COUNTRY_CODES.map(c => (
                            <option key={c.code} value={c.code}>
                                {c.flag} {c.dial}
                            </option>
                        ))}
                    </select>
                    <input
                        type="tel"
                        className={styles.phoneInput}
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder={t('settings.profile.phonePlaceholder')}
                    />
                </div>
                <p className={shared.helperText}>{t('settings.profile.phoneHelper')}</p>
            </div>

            <div className={shared.actions}>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    isDisabled={!isDirty || isSaving}
                >
                    {t('settings.profile.saveChanges')}
                </Button>
                <Button
                    variant="secondary"
                    onClick={handleCancel}
                    isDisabled={!isDirty || isSaving}
                >
                    {t('settings.profile.cancel')}
                </Button>
            </div>
        </div>
    );
}

export default ProfileSection;